import * as React from 'react'

import { createPortal } from 'react-dom'

import { cn } from '~/client/src/shared/utils/cn'

import { useSidePanelOverlayRoot } from '../SidePanel/SidePanelOverlayContext'

/**
 * `fill` spans the host panel from `top-[10%]` down — for list pickers and field
 * selectors that need a tall, scrollable body. `content` bottom-anchors the sheet
 * and lets it hug its content (capped at `max-h-[90%]`, then scrolls), so a short
 * form leaves no empty gap above the footer. Shared with `PickerSheetLayout` and
 * `PopoverTagPicker` so the sheet chrome stays in lockstep with the sheet.
 */
export type SheetHeightMode = 'fill' | 'content'

interface IProps {
  isOpen: boolean
  onClose: () => void
  /** See {@link SheetHeightMode}. Defaults to `fill`. */
  heightMode?: SheetHeightMode
  children: React.ReactNode
}

const FullScreenPickerSheet: React.FC<IProps> = ({
  isOpen,
  onClose,
  heightMode = 'fill',
  children,
}) => {
  const overlayRoot = useSidePanelOverlayRoot()

  if (!isOpen) return null

  const content = (
    <>
      <div
        className="tw:absolute tw:inset-0 tw:z-50 tw:bg-black/40 tw:pointer-events-auto"
        onClick={onClose}
      />
      <div
        className={cn(
          'tw:absolute tw:left-0 tw:right-0 tw:bottom-0 tw:z-50 tw:bg-md3-surface tw:rounded-t-xl tw:overflow-hidden tw:flex tw:flex-col tw:pointer-events-auto',
          heightMode === 'content' ? 'tw:max-h-[90%]' : 'tw:top-[10%]',
        )}
      >
        {children}
      </div>
    </>
  )

  // Render into the side panel's overlay root so the sheet fills the whole
  // panel, not whatever positioned ancestor the trigger field happens to sit
  // in. Falls back to inline rendering when there is no side panel host.
  return overlayRoot?.current
    ? createPortal(content, overlayRoot.current)
    : content
}

export default FullScreenPickerSheet
